import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FamilleGemrcnSavedSupplier, FamillesGemrcnService} from '../../core/services/entities/familles-gemrcn.service';
import {FamilleGemrcnDTO} from "../../core/dtos/famille-gemrcn-dto";
import {Subscription} from "rxjs";
import {Auth2Service} from "../../core/services/security/auth2.service";
import {UtilsService} from "../../core/utils/utils.service";
import {saveAs} from "file-saver";
import {HELP_FOLDERS, MSG_KEY, MSG_SEVERITY} from "../../core/constants";
import {ToastService} from "../../core/services/technique/toast.service";
import {InternationalizationService} from "../../core/services/i8n/i8n.service";
import {ResponseWrapper} from "../../core/suppliers/wrappers/response-wrapper";
import {IsDeletableObject} from "../../core/models/is-deletable-object";
import {GraphQLService} from "../../core/services/technique/graphql.service";
import {confirm} from "devextreme/ui/dialog";
import {DxTooltipComponent} from "devextreme-angular";

@Component({
  selector: 'yo-familles-gemrcn',
  templateUrl: './familles-gemrcn.component.html',
  styleUrls: ['./familles-gemrcn.component.scss']
})
export class FamillesGemrcnComponent implements OnInit, OnDestroy {

  @ViewChild("importTooltip") importTooltip: DxTooltipComponent;

  subLog: Subscription;
  subFamilleGemrcnSaved: Subscription;
  subFamillesGemrcn: Subscription;

  logged: boolean = false;

  familles: FamilleGemrcnDTO[] = [];

  selectedRows: number[] = [];
  errors: any[] = [];

  displayImportPopupErrors: boolean = false;
  displayDeletePopupErrors: boolean = false;

  pathHelpFile: string = HELP_FOLDERS.REFERENTIEL + '/familles-gemrcn/familles-gemrcn';

  constructor(private auth2Svc: Auth2Service,
              private i8nSvc: InternationalizationService,
              private graphQlSvc: GraphQLService,
              public utilsSvc: UtilsService,
              public familleGemrcnSvc: FamillesGemrcnService,
              public toastSvc: ToastService) {
  }

  ngOnInit() {
    this.initLoggedSubscription();
    this.initFamillesGemrcnSubscription();
    this.initFamillesGemrcnSavedSubscription();
  }

  ngOnDestroy(): void {
    this.utilsSvc.unsubscribe(this.subLog);
    this.utilsSvc.unsubscribe(this.subFamilleGemrcnSaved);
    this.utilsSvc.unsubscribe(this.subFamillesGemrcn);
  }

  initLoggedSubscription = (): void => {
    this.subLog = this.auth2Svc.isLoggedIn$
      .subscribe(res => this.logged = res);
  }

  initFamillesGemrcnSubscription = (): void => {
    const idsSites: number[] = this.auth2Svc.utilisateur.sites.map(s => s.id);
    this.subFamillesGemrcn = this.graphQlSvc.sendQuery(`
      {
        allFamillesGemrcn(filters: {
          siteIds: [${idsSites}]
        }) {
            id,
            code,
            abreviation,
            libelle,
            actif,
            ordre,
            site {
                id,
                libelle,
            },
            couleur {
                id,
                hexa,
                code,
                libelle
            }
        }
      }
    `)
      .subscribe((response: any) => {
        this.familles = response.allFamillesGemrcn;
      });
  }

  initFamillesGemrcnSavedSubscription = (): void => {
    this.subFamilleGemrcnSaved = this.familleGemrcnSvc.familleGemrcnSaved$
      .subscribe((response: FamilleGemrcnSavedSupplier) => {
        if (response.isUpdate) {
          const idx: number = this.familles.findIndex(famille => famille.id === response.familleGemrcn.id);
          this.familles[idx] = response.familleGemrcn;
        } else {
          this.familles.push(response.familleGemrcn);
        }
      });
  };

  openDialogEdition = (familleGemrcn: FamilleGemrcnDTO): void => {
    this.familleGemrcnSvc.announceOpenDialogEdition(familleGemrcn);
  };

  calculateColorValue = (familleGemrcn: FamilleGemrcnDTO): string => {
    return familleGemrcn?.couleur?.libelle
  };

  calculateSiteValue = (familleGemrcn: FamilleGemrcnDTO): string => {
    return familleGemrcn?.site?.libelle
  };

  /**
   * Exporter les fichiers au format xlsx
   */
  exportXslx = () => {
    this.familleGemrcnSvc.exportFamilleGemrcnByFilters(this.selectedRows).subscribe(response => {
      const filename = `familles-gemrcn-export-${new Date().getTime()}.xlsx`;
      const blob = new Blob([response], {type: 'application/vnd.ms-excel'});
      this.selectedRows = [];
      saveAs(blob, filename);
    });
  }

  importXslx = async ($event: any) => {
    this.errors = [];
    if ($event.target.files && $event.target.files.length) {
      const file: File = $event.target.files[0];
      this.familleGemrcnSvc.importFamilleGemrcnFromXlsx(file)
        .subscribe(response => {
          const res: any = response.one;
          if (res.allElementsImported)
            this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Le fichier a été importé avec succès`);
          else {
            for (const item of Object.entries(res.reports)) {
              let values: any[] = Array.of(item[1]);
              const labelsError: any[] = values.map(val => val.map(v => this.i8nSvc.getLabelFromCode(v.code, v.args)).join(', '));
              this.errors.push({infosLine: item[0], labelsError});
            }
          }
          for (const item of res.elementsImported) {
            let index = this.familles.findIndex(equipe => equipe.id == item.id);
            if (index >= 0)
              this.familles[index] = item;
            else
              this.familles.push(item)
          }

          if (this.errors != null && this.errors.length > 0)
            this.displayImportErrors();

          $event.target.value = "";
          this.importTooltip.instance.hide();
        });
    }
  }

  displayImportErrors = (): void => {
    this.displayImportPopupErrors = true;
  }

  displayDeleteErrors = (): void => {
    this.displayDeletePopupErrors = true;
  }

  closeErrors = (): void => {
    this.errors = [];
    this.displayImportPopupErrors = false;
    this.displayDeletePopupErrors = false;
  }

  deleteValues = async (): Promise<void> => {
    this.errors = [];
    let idsToDeleteInTable = Object.assign([], this.selectedRows);

    let isDeletionValidated: boolean = await confirm(this.i8nSvc.getLabelFromCode(idsToDeleteInTable.length > 1 ? "CONFIRM_DELETION_PLURAL" : "CONFIRM_DELETION_SINGULAR", null), this.i8nSvc.getLabelFromCode("Suppression", null));

    if (!isDeletionValidated)
      return new Promise(null);

    this.familleGemrcnSvc.deleteFamilleGemrcnByFilters(this.selectedRows).subscribe(response => {
      const res: any = (response as ResponseWrapper<IsDeletableObject>).one;

      if (res) {
        if (res.messagesErrorList && res.messagesErrorList.length > 0) {
          for (const error of res.messagesErrorList) {
            const labelsError: any = this.i8nSvc.getLabelFromCode(error.code, null);
            const infosLine: string = labelsError.replace('{}', error.args);
            this.errors.push({infosLine});
            const elementToKeep = this.familles.find(famille => famille.code === error.args);
            idsToDeleteInTable = idsToDeleteInTable.filter(id => elementToKeep.id !== id);
          }
          this.displayDeleteErrors();
        } else {
          this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, idsToDeleteInTable.length > 1 ? `Les familles ont bien été supprimées` : `La famille a bien été supprimée`);
        }
        this.familles = this.familles.filter(famille => !idsToDeleteInTable.find(id => id === famille.id));
      }
    });
  }

  canDelete = (): boolean => this.selectedRows.length > 0;

}
